import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PricelistForm from './PricelistForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import { CategoryPill } from '../../../shared/Pill';
import { useRrpSettings } from '../../product/Products/productReportShared';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { formatDate } from '../../../shared/tenantClock';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

const STATUS_LABELS = { draft: 'Draft', published: 'Published', deactivated: 'Deactivated' };
// Fully reversible — any status can move to any other (matches the
// backend's setPricelistStatus, pricelistController.js). Row menu offers a
// "Move to X" action per other status; Edit stays Draft-only regardless.
const MOVE_TO_LABEL = { draft: 'Move to Draft', published: 'Publish', deactivated: 'Deactivate' };
const ALL_STATUSES = ['draft', 'published', 'deactivated'];

// Identifies this screen's saved filters (SavedFilterTabs) from any other
// screen's — same shared saved_filters table/pattern as Prices
// (Page 3-1). Group has exactly 3 values (B2B/Dealer/Retail) — a tab per
// Group is cheap and stable, e.g. "Retail" vs "Dealer" at a click. Product
// Price Group is hierarchical, so it's tab-*able* (in the drawer) but not
// the primary use case tabs solve here. Name is free-text and unbounded —
// deliberately NOT part of the saveable filter, same split Price
// Adjustments/Products already use (search box ad hoc, tabs for the fixed-
// value dimensions).
const FILTER_SCREEN_KEY = 'pricelists';
const DEFAULT_FILTERS = { status: 'all', customerPriceGroupId: '', productPriceGroupId: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Pricelist 2.0's landing page (Page #3-18) — named, standing definitions
// ("Retail Pricelist 2026"), not ad hoc filter tabs. Generating one is a row
// action, reached at /pricelists/:id/generate — see Pricelist2Page.js,
// which now loads its scope from a saved record instead of a form.
function PricelistTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [nameSearch, setNameSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [customerPriceGroups, setCustomerPriceGroups] = useState([]);
  const [productPriceGroups, setProductPriceGroups] = useState([]);
  const [uoms, setUoms] = useState([]);

  useDefaultPageSize(setLimit);
  // Show RRP only means something to a tenant that tracks RRP (locked
  // Company Settings' track_rrp), so the column is hidden otherwise.
  const { trackRrp } = useRrpSettings();
  const colCount = trackRrp ? 9 : 8;

  const applyFilters = (values) => { setFilters(values); setPage(1); };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/pricelists', {
        params: {
          status: filters.status,
          customer_price_group_id: filters.customerPriceGroupId || undefined,
          product_price_group_id: filters.productPriceGroupId || undefined,
          name: nameSearch || undefined,
          page,
          limit,
        },
      });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [filters, nameSearch, page, limit]);

  useEffect(() => {
    axios.get('/api/customer-price-groups', { params: { status: 'active' } })
      .then((res) => setCustomerPriceGroups(res.data.data || []))
      .catch(() => setCustomerPriceGroups([]));
    axios.get('/api/product-price-groups')
      .then((res) => setProductPriceGroups((res.data.data || []).filter((g) => g.is_active)))
      .catch(() => setProductPriceGroups([]));
    axios.get('/api/uoms', { params: { status: 'active' } })
      .then((res) => setUoms(res.data.data || []))
      .catch(() => setUoms([]));
  }, []);

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/pricelists/${editItem.id}`, data);
      } else {
        await axios.post('/api/pricelists', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  const handleStatusChange = async (item, status) => {
    try {
      await axios.patch(`/api/pricelists/${item.id}/status`, { status });
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Status change failed');
    }
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Delete Pricelist "${item.name}"?`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/pricelists/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  // The drawer's field list for SavedFilterTabs — Group/Product Price Group/Status,
  // the fixed-value dimensions worth naming and switching between. Name
  // search stays outside, as its own always-visible box below.
  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Customer Price Group</label><br />
        <select className="styled-input" value={draft.customerPriceGroupId} onChange={(e) => setDraftField('customerPriceGroupId', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All groups</option>
          {customerPriceGroups.filter((g) => g.is_active).map((g) => (
            <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Product Price Group</label><br />
        <select className="styled-input" value={draft.productPriceGroupId} onChange={(e) => setDraftField('productPriceGroupId', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">Any</option>
          <option value="all">All products</option>
          {productPriceGroups.map((c) => (
            <option key={c.id} value={c.id}>{'   '.repeat(c.depth || 0)}{c.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All statuses</option>
          {ALL_STATUSES.map((status) => (
            <option key={status} value={status}>{STATUS_LABELS[status]}</option>
          ))}
        </select>
      </div>
    </>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Pricelists</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: showForm ? '#9fb3c8' : '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer' }}
            >
              Add Pricelist
            </button>
          )}
        </div>
      </div>
      <div style={{ fontSize: 13, color: '#888', margin: '4px 24px 0' }}>
        Named, standing pricelist definitions — resolved live against current Retail Prices and Prices each time you generate one.
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Search name..."
            value={nameSearch}
            onChange={(e) => { setNameSearch(e.target.value); setPage(1); }}
            style={{ width: 220 }}
          />
          {nameSearch && (
            <button type="button" onClick={() => { setNameSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <PricelistForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            customerPriceGroups={customerPriceGroups}
            productPriceGroups={productPriceGroups}
            uoms={uoms}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: trackRrp ? '21%' : '23%' }} />
            <col style={{ width: trackRrp ? '15%' : '17%' }} />
            <col style={{ width: trackRrp ? '15%' : '17%' }} />
            <col style={{ width: trackRrp ? '9%' : '10%' }} />
            <col style={{ width: trackRrp ? '9%' : '10%' }} />
            <col style={{ width: trackRrp ? '9%' : '10%' }} />
            {trackRrp && <col style={{ width: '8%' }} />}
            <col style={{ width: trackRrp ? '9%' : '8%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Customer Price Group</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Product Price Group</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>UoM</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Valid From</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Valid To</th>
              {trackRrp && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Show RRP</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && item.status === 'draft') handleEdit(item); }}
                style={{ cursor: !showForm && item.status === 'draft' ? 'pointer' : 'default' }}
                title={!showForm && item.status === 'draft' ? 'Click to edit' : undefined}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.name}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.customer_price_group?.name || '—'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.product_price_group?.name || 'All products'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.uom?.name || 'All UoMs'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{formatDate(item.valid_from) || '—'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{formatDate(item.valid_to) || '—'}</td>
                {trackRrp && <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 14 }}>{item.show_rrp ? 'Yes' : 'No'}</td>}
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <CategoryPill value={STATUS_LABELS[item.status] || item.status} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'generate', label: 'Generate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => navigate(`/pricelists/${item.id}/generate`) },
                      ...(item.status === 'draft' ? [{ key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) }] : []),
                      ...ALL_STATUSES.filter((status) => status !== item.status).map((status) => ({
                        key: `status-${status}`,
                        label: MOVE_TO_LABEL[status],
                        disabled: showForm,
                        disabledTitle: showForm ? 'Finish editing first' : undefined,
                        onClick: () => handleStatusChange(item, status),
                      })),
                      ...(item.status === 'draft' ? [{
                        key: 'delete',
                        label: 'Delete',
                        danger: true,
                        disabled: showForm,
                        disabledTitle: showForm ? 'Finish editing first' : undefined,
                        onClick: () => handleDelete(item),
                      }] : []),
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Pricelists"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default PricelistTable;
